/* SpawnAnything — weather on the client (feature:weather): choice in the setup panel, the resolved spec rides in
   every `setup` message to the worker, GPU rain / snow / sand / wind streaks (same camera-wrapped technique as the
   ambient particles in main.js), darker sky and denser fog, lightning bolts with flash, HUD chip with wind arrow.
   Gameplay effects live in src/sim/features/weather.js. */
import * as THREE from 'three';
import './weather.css';
import { TYPES } from '../game/voxel.js';
import { WEATHER, STYLE_WEATHER, EV_WX_BOLT, resolveWeather, sanitizeWeather } from '../sim/features/weather.js';

export { EV_WX_BOLT };

const CHOICES = [
  ['auto', 'Passend zur Welt'], ['klar', 'Klar'], ['regen', 'Regen'], ['gewitter', 'Gewitter'], ['sturm', 'Sturm / Wind'],
  ['sandsturm', 'Sandsturm'], ['nebel', 'Nebel'], ['schnee', 'Schneesturm'], ['zufall', 'Zufällig'],
];
// lightning rods: machines and armour plate (by type key or recipe, so era packs count too)
const METAL = new Set(['panzer', 'optimus', 'megatron', 'mechhase', 'roboter', 'mech_pilot', 'robohund', 'cyborg']);
const isMetal = A => !!A && (METAL.has(A.cfg && A.cfg.type) || METAL.has(A.ty && A.ty.recipe) || !!(TYPES[A.cfg && A.cfg.type] || {}).metal);

/* look per weather at full intensity: particle type + count, sky tint [colour, mix, brightness], light and fog factors */
const LOOK = {
  regen: { amb: 'rain', n: 2600, sky: [0x6a7684, 0.8, 0.32], hemi: 0.62, sun: 0.3, fog: [0.75, 0.85] },
  gewitter: { amb: 'rain', n: 3600, sky: [0x39424f, 0.9, 0.12], hemi: 0.48, sun: 0.14, fog: [0.7, 0.8] },
  sturm: { amb: 'streak', n: 900, sky: [0x8d969e, 0.5, 0.6], hemi: 0.85, sun: 0.65, fog: [0.85, 0.9] },
  sandsturm: { amb: 'sand', n: 2600, sky: [0xcf9f5c, 0.7, 0.65], hemi: 0.85, sun: 0.5, fog: [0.4, 0.6] },
  nebel: { amb: null, n: 0, sky: [0xb4bcc4, 0.85, 0.8], hemi: 0.9, sun: 0.45, fog: [0.2, 0.42] },
  schnee: { amb: 'blizzard', n: 3000, sky: [0xc6d0da, 0.7, 0.8], hemi: 0.85, sun: 0.45, fog: [0.5, 0.65] },
};
/* weather particles, same fields as AMB_TYPES in main.js plus fall speed, wind share, streak length and opacity */
const WX_AMB = {
  rain: { fall: 16, windMul: 0.7, sway: 0, size: 0.028, len: 0.75, color: [0.55, 0.6, 0.7], opacity: 0.35 },
  blizzard: { fall: 2.6, windMul: 1.2, sway: 0.9, size: 0.085, len: 0.1, color: [1.2, 1.22, 1.28], opacity: 1 },
  sand: { fall: -0.15, windMul: 1.7, sway: 0.6, size: 0.055, len: 0.3, color: [1.0, 0.8, 0.52], opacity: 0.75, hMul: 0.45 },
  streak: { fall: 0.2, windMul: 2.4, sway: 0.3, size: 0.018, len: 2.4, color: [1, 1, 1], opacity: 0.2, hMul: 0.55 },
};
const ICONS = {
  klar: '<circle cx="12" cy="12" r="4.2"/><path d="M12 2.5v2.8M12 18.7v2.8M2.5 12h2.8M18.7 12h2.8M5.3 5.3l2 2M16.7 16.7l2 2M5.3 18.7l2-2M16.7 7.3l2-2"/>',
  regen: '<path d="M7 15.5h9.5a3.5 3.5 0 0 0 .4-7 5 5 0 0 0-9.6 1.3A2.9 2.9 0 0 0 7 15.5z"/><path d="M8.5 18l-1 2.5M12.5 18l-1 2.5M16.5 18l-1 2.5"/>',
  gewitter: '<path d="M7 14h9.5a3.5 3.5 0 0 0 .4-7 5 5 0 0 0-9.6 1.3A2.9 2.9 0 0 0 7 14z"/><path d="M12.5 14.5l-2.5 4h3l-2 3.5"/>',
  sturm: '<path d="M3 9h11a2.5 2.5 0 1 0-2.5-2.5M3 13h15a2.5 2.5 0 1 1-2.5 2.5M3 17h7"/>',
  sandsturm: '<path d="M3 9h11a2.5 2.5 0 1 0-2.5-2.5M3 13h15a2.5 2.5 0 1 1-2.5 2.5"/><path d="M5 17.5h.01M9 19h.01M13 17.5h.01M17 19.5h.01"/>',
  nebel: '<path d="M4 8h16M2.5 12h14M6 16h15M4 20h10"/>',
  schnee: '<path d="M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9M9.5 4.5L12 7l2.5-2.5M9.5 19.5L12 17l2.5 2.5"/>',
};
const svg = (inner, cls) => `<svg class="${cls}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`;
const clamp = (v, a, b) => v < a ? a : v > b ? b : v;

/**
 * deps: scene, camera, worker, sky (scene.background colour), hemi, sun, uTime, uGTime, view() = camCur, groundH,
 * quality() = Q, fx() / debris() = particle systems, shake, getArmies(), isSetup(), onChange() = re-send the setup.
 */
export function createWeather(deps) {
  const { scene, worker, sky, hemi, sun, uTime, uGTime, view, groundH, quality, getArmies, isSetup, onChange } = deps;
  let choice = 'auto';
  try { const c = localStorage.getItem('aw-weather'); if (c && CHOICES.some(o => o[0] === c)) choice = c; } catch (e) { }
  let spec = null, W = null, look = null, sent = false, specKey = '';
  const base = { sky: new THREE.Color(), hemi: 1, sun: 1, near: 200, far: 900, ok: false };

  /* ---- setup: every setup message carries the resolved weather (same seed => same weather on every client) ---- */
  const post = worker.postMessage.bind(worker);
  worker.postMessage = (...a) => {
    const m = a[0];
    if (m && m.cmd === 'setup') {
      const A = getArmies();
      // multiplayer: the host's choice arrives with the setup, so every client resolves the same weather
      const ch = typeof m.weatherChoice === 'string' && CHOICES.some(o => o[0] === m.weatherChoice) ? m.weatherChoice : choice;
      const metal = [0, 1].map(t => A.some((G, g) => G && (G.team === undefined ? g === t : G.team === t) && isMetal(G))); // any metal troop of the side
      m.weather = resolveWeather(ch, deps.styleKey(), m.seed, metal);
      sent = true; setActive(m.weather);
    }
    return post(...a);
  };

  /* ---- setup panel: choice below the terrain picker ---- */
  const box = document.createElement('div');
  box.className = 'weather-box';
  box.innerHTML = `<label class="h" for="weather">Wetter</label><select class="qsel" id="weather" style="margin-top:8px">${CHOICES.map(([k, n]) => `<option value="${k}">${n}</option>`).join('')}</select><p class="sdesc" id="weatherDesc"></p>`;
  const terrainBlock = document.getElementById('terrain')?.closest('.panel > div');
  if (terrainBlock) terrainBlock.after(box); else document.getElementById('panel')?.appendChild(box);
  const sel = box.querySelector('select'), desc = box.querySelector('#weatherDesc');
  sel.value = choice;
  sel.addEventListener('change', () => {
    choice = sel.value;
    try { localStorage.setItem('aw-weather', choice); } catch (e) { }
    if (sent && isSetup()) onChange();
  });

  /* ---- HUD chip: icon, wind arrow (relative to the view), name ---- */
  const hud = document.createElement('span');
  hud.className = 'weather-hud';
  hud.innerHTML = `<span class="weather-ico"></span><span class="weather-arrow">${svg('<path d="M12 20V5M6.5 10.5L12 5l5.5 5.5"/>', 'weather-svg')}</span><span class="weather-name"></span>`;
  document.querySelector('.top .meta')?.prepend(hud);
  const hIco = hud.querySelector('.weather-ico'), hArrow = hud.querySelector('.weather-arrow'), hName = hud.querySelector('.weather-name');
  let arrowAng = 1e9;

  function setActive(w) {
    const key = w.key + ':' + w.k + ':' + w.dir;
    if (key === specKey) return;
    specKey = key; spec = w; W = sanitizeWeather(w); look = W ? LOOK[w.key] : null;
    const P = WEATHER[w.key], light = w.k < 0.66 && w.key !== 'klar' ? ' (leicht)' : '';
    desc.textContent = (choice === 'auto' ? `Passend zur Welt: ${P.name}${light}. ` : choice === 'zufall' ? `Zufällig, diesmal: ${P.name}${light}. ` : '') + P.desc;
    hIco.innerHTML = svg(ICONS[w.key], 'weather-svg');
    hName.textContent = P.name + light;
    hArrow.hidden = !W || W.ws < 0.5; arrowAng = 1e9;
    hud.title = 'Wetter: ' + P.name + light + (W && W.ws >= 0.5 ? ` · Wind ${Math.round(W.ws)} m/s, der Pfeil zeigt, wohin er weht` : '');
    buildParticles(); applyFog(); applyLight(0);
  }

  /* ---- sky, light, fog (from the style's values, captured after applyStyle / setFog) ---- */
  const tint = new THREE.Color(), FLASH = new THREE.Color(0xdde6ff);
  const lum = c => c.r * 0.2126 + c.g * 0.7152 + c.b * 0.0722;
  function applyLight(flash) {
    if (!base.ok) return;
    const k = W ? W.k : 0;
    sky.copy(base.sky);
    if (look) {
      tint.setHex(look.sky[0]);
      tint.multiplyScalar(lum(base.sky) * look.sky[2] / Math.max(0.02, lum(tint)));
      sky.lerp(tint, look.sky[1] * k);
    }
    if (flash > 0) sky.lerp(FLASH, flash * 0.55);
    if (scene.fog) scene.fog.color.copy(sky);
    hemi.intensity = base.hemi * (look ? 1 - (1 - look.hemi) * k : 1) + flash * 2.4;
    sun.intensity = base.sun * (look ? 1 - (1 - look.sun) * k : 1) + flash * 1.2;
  }
  function applyFog() {
    if (!scene.fog || !base.fogOk) return;
    const k = W ? W.k : 0, f = look ? look.fog : [1, 1];
    scene.fog.near = base.near * (1 - (1 - f[0]) * k);
    scene.fog.far = Math.max(scene.fog.near + 40, base.far * (1 - (1 - f[1]) * k));
  }

  /* ---- particles: wrapped around the camera, fully on the GPU ---- */
  let amb = null, ambKey = '';
  function buildParticles() {
    const Q = quality(), T = look && look.amb ? WX_AMB[look.amb] : null;
    const n = T ? Math.round(look.n * (0.4 + 0.6 * W.k) * Q.ambMul) : 0;
    const key = T ? look.amb + ':' + n + ':' + W.wx + ':' + W.wz : '';
    if (key === ambKey) return;
    ambKey = key;
    if (amb) { scene.remove(amb.mesh); amb.mesh.geometry.dispose(); amb.mesh.material.dispose(); amb = null; }
    if (!T || !n) return;
    const box = new THREE.BoxGeometry(1, 1, 1);
    const g = new THREE.InstancedBufferGeometry();
    g.index = box.index; g.setAttribute('position', box.getAttribute('position'));
    const seed = new Float32Array(n * 4); for (let i = 0; i < seed.length; i++) seed[i] = Math.random();
    g.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seed, 4)); g.instanceCount = n;
    const vel = new THREE.Vector3(W.wx * T.windMul, -T.fall, W.wz * T.windMul);
    const u = { uT: uTime, uO: { value: new THREE.Vector3() }, uBox: { value: new THREE.Vector3(100, 40, 100) }, uVel: { value: vel }, uDir: { value: vel.clone().normalize() }, uSway: { value: T.sway }, uSize: { value: T.size }, uLen: { value: T.len }, uDist: { value: 50 } };
    const m = new THREE.MeshBasicMaterial({ color: new THREE.Color(...T.color), fog: true, transparent: T.opacity < 1, opacity: T.opacity, depthWrite: T.opacity >= 1 });
    m.onBeforeCompile = sh => {
      Object.assign(sh.uniforms, u);
      sh.vertexShader = sh.vertexShader.replace('void main() {', 'uniform float uT, uSway, uSize, uLen, uDist; uniform vec3 uO, uBox, uVel, uDir; attribute vec4 aSeed;\nvoid main() {')
        .replace('#include <begin_vertex>', `vec3 p = aSeed.xyz * uBox + uVel * uT * (0.8 + aSeed.w * 0.4);
  p.x += sin(uT * 1.3 + aSeed.w * 20.0) * uSway; p.z += cos(uT * 1.1 + aSeed.w * 13.0) * uSway;
  vec2 rel = mod(p.xz - uO.xz + uBox.xz * 0.5, uBox.xz) - uBox.xz * 0.5;
  float y = mod(p.y, uBox.y);
  float sc = uSize * max(1.0, uDist / 45.0);
  vec3 transformed = vec3(position.x, 0.0, position.z) * sc + uDir * position.y * (sc + uLen * max(1.0, uDist / 60.0)) + vec3(uO.x + rel.x, y + uO.y, uO.z + rel.y);`);
    };
    m.customProgramCacheKey = () => 'weather-amb';
    const mesh = new THREE.Mesh(g, m); mesh.frustumCulled = false; mesh.renderOrder = 1;
    scene.add(mesh);
    amb = { mesh, u, T };
  }

  /* ---- lightning bolts: jagged instanced segments (bright core + additive halo) that flicker out ---- */
  const BMAX = 256;
  const bGeo = new THREE.BoxGeometry(1, 1, 1);
  const bT = new THREE.InstancedBufferAttribute(new Float32Array(BMAX).fill(-1e6), 1); bT.setUsage(THREE.DynamicDrawUsage);
  bGeo.setAttribute('aT0', bT);
  const bMat = new THREE.MeshBasicMaterial({ color: 0xffffff, fog: false, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false });
  bMat.onBeforeCompile = sh => {
    sh.uniforms.uGT = uGTime;
    sh.vertexShader = sh.vertexShader.replace('void main() {', 'uniform float uGT; attribute float aT0;\nvoid main() {')
      .replace('#include <begin_vertex>', `float age = uGT - aT0;
  float vis = step(0.0, age) * step(age, 0.42) * (age < 0.09 ? 1.0 : step(0.4, fract(age * 13.0)) * (1.0 - age / 0.42));
  vec3 transformed = position * vis;`);
  };
  bMat.customProgramCacheKey = () => 'weather-bolt';
  const bMesh = new THREE.InstancedMesh(bGeo, bMat, BMAX); bMesh.frustumCulled = false; bMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  bMesh.setColorAt(0, new THREE.Color()); bMesh.renderOrder = 3;
  scene.add(bMesh);
  let bHead = 0;
  const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _v = new THREE.Vector3(), _s = new THREE.Vector3(), _e = new THREE.Euler();
  const CORE = new THREE.Color(2.6, 2.7, 3.2), HALO = new THREE.Color(0.22, 0.3, 0.6);
  function seg(x0, y0, z0, x1, y1, z1, w, col) {
    const i = bHead; bHead = (bHead + 1) % BMAX;
    const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0, L = Math.hypot(dx, dy, dz) || 0.01;
    _e.set(-Math.atan2(dy, Math.hypot(dx, dz)), Math.atan2(dx, dz), 0, 'YXZ'); _q.setFromEuler(_e);
    _m.compose(_v.set((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2), _q, _s.set(w, w, L + w));
    bMesh.setMatrixAt(i, _m); bMesh.setColorAt(i, col); bT.array[i] = uGTime.value;
  }
  function jag(x0, y0, z0, x1, y1, z1, n, amp, w, branches) {
    let px = x0, py = y0, pz = z0;
    for (let s = 1; s <= n; s++) {
      const f = s / n, a = s === n ? 0 : amp * (1 - f * 0.6);
      const x = x0 + (x1 - x0) * f + (Math.random() - 0.5) * a, y = y0 + (y1 - y0) * f, z = z0 + (z1 - z0) * f + (Math.random() - 0.5) * a;
      seg(px, py, pz, x, y, z, w * 4, HALO); seg(px, py, pz, x, y, z, w, CORE);
      if (branches > 0 && s > 2 && s < n - 2 && Math.random() < 0.3) {
        branches--;
        const bl = (y0 - y1) * 0.25, ang = Math.random() * 6.283;
        jag(x, y, z, x + Math.cos(ang) * bl * 0.6, y - bl, z + Math.sin(ang) * bl * 0.6, 5, amp * 0.6, w * 0.5, 0);
      }
      px = x; py = y; pz = z;
    }
  }
  let flashAt = -1e9, flashAmp = 0, lightDirty = false;
  function bolt(x, gy, z, r, top) {
    const h = 55 + Math.random() * 25, ox = (Math.random() - 0.5) * 16, oz = (Math.random() - 0.5) * 16;
    jag(x + ox, top + h, z + oz, x, top, z, 16, 6, 0.22, 2);
    bMesh.instanceMatrix.needsUpdate = true; bMesh.instanceColor.needsUpdate = true; bT.needsUpdate = true;
    const fx = deps.fx(), debris = deps.debris(), g0 = groundH(x, z);
    for (let i = 0; i < 26; i++) {
      const a = Math.random() * 6.28, sp = 3 + Math.random() * 9;
      fx.spawn(x, top, z, Math.cos(a) * sp, 2 + Math.random() * 7, Math.sin(a) * sp, 0.25 + Math.random() * 0.3, 0.12 + Math.random() * 0.12, 6, 8, 1, 1.7, 1.9, 2.6);
    }
    for (let i = 0; i < 18; i++) {
      const a = (i / 18) * 6.28, sp = r * (1.3 + Math.random() * 0.5);
      fx.spawn(x, g0 + 0.3, z, Math.cos(a) * sp, 0.6 + Math.random(), Math.sin(a) * sp, 0.9 + Math.random() * 0.5, 0.6 + Math.random() * 0.5, 0, 1.5, 2, 0.4, 0.4, 0.44);
    }
    for (let i = 0; i < 10; i++) {
      const a = Math.random() * 6.28, sp = 2 + Math.random() * 4;
      debris.spawn(x, g0 + 0.2, z, Math.cos(a) * sp, 4 + Math.random() * 5, Math.sin(a) * sp, 8, 0.12 + Math.random() * 0.12, 18, 6, 0, 0.2, 0.17, 0.13, g0);
    }
    const c = view(), d = Math.hypot(x - c.tx, z - c.tz);
    flashAt = performance.now(); flashAmp = clamp(1.25 - d / 350, 0.35, 1);
    deps.shake(r * 1.4, x, z);
  }

  return {
    /** Setup fields the multiplayer host sends to every client (see setupExtras in main.js). */
    extras() { return { weatherChoice: choice }; },
    /** Event from the worker (handleEvents in main.js). */
    event(ev, o) { if (ev[o] === EV_WX_BOLT) bolt(ev[o + 1], ev[o + 2], ev[o + 3], ev[o + 4], ev[o + 5]); },
    /** After applyStyle: capture the style's sky and light, then weather them again. */
    restyle() {
      base.sky.copy(sky); base.hemi = hemi.intensity; base.sun = sun.intensity; base.ok = true;
      applyLight(0);
      // "Passend zur Welt" follows the style while the battle is being set up
      if (sent && choice === 'auto' && isSetup() && spec && (STYLE_WEATHER[deps.styleKey()] || ['klar'])[0] !== spec.key) onChange();
    },
    /** After setFog: capture the style's fog range, then thicken it. */
    fog() { if (!scene.fog) return; base.near = scene.fog.near; base.far = scene.fog.far; base.fogOk = true; applyFog(); },
    /** Every frame: particles follow the camera, lightning flash, HUD arrow. */
    frame(now) {
      buildParticles(); // cheap key check; rebuilds after a quality change
      const c = view();
      if (amb) {
        const B = clamp(c.dist * 1.5, 40, 320);
        amb.u.uO.value.set(c.tx, groundH(c.tx, c.tz), c.tz);
        amb.u.uBox.value.set(B, clamp(c.dist * 0.9, 20, 140) * (amb.T.hMul || 1), B);
        amb.u.uDist.value = c.dist;
      }
      const age = (now - flashAt) / 1000;
      if (age < 0.5) {
        const fl = flashAmp * (1 - age / 0.5) * (age < 0.07 || (age > 0.14 && age < 0.2) ? 1 : 0.3);
        applyLight(fl); lightDirty = true;
      } else if (lightDirty) { applyLight(0); lightDirty = false; }
      if (W && W.ws >= 0.5) {
        // screen angle of the wind: up = view direction, clockwise positive
        const fx = -Math.sin(c.yaw), fz = -Math.cos(c.yaw), rx = Math.cos(c.yaw), rz = -Math.sin(c.yaw);
        const a = Math.atan2(W.wx * rx + W.wz * rz, W.wx * fx + W.wz * fz);
        if (Math.abs(a - arrowAng) > 0.03) { arrowAng = a; hArrow.style.transform = `rotate(${a.toFixed(3)}rad)`; }
      }
    },
  };
}
